'use client';
import React, { useState } from 'react';
import { Search, Filter, ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownLeft, Trophy, Gift, Lock, Unlock } from 'lucide-react';

interface TransactionTableProps {
  currency: string;
  symbol: string;
}

const allTransactions = [
  { id: 'tx-001', type: 'prize', description: 'Prize: National Trivia Q2 — 1st Place', amount: 2500.00, status: 'completed', date: '2026-08-08', time: '14:32', ref: 'BB-TRV-2847' },
  { id: 'tx-002', type: 'entry', description: 'Entry: National Trivia Championship Q3', amount: -10.00, status: 'completed', date: '2026-08-08', time: '11:15', ref: 'BB-ENT-9012' },
  { id: 'tx-003', type: 'deposit', description: 'Deposit via Stripe — Visa ending 4242', amount: 100.00, status: 'completed', date: '2026-08-07', time: '18:45', ref: 'BB-DEP-7821' },
  { id: 'tx-004', type: 'withdrawal', description: 'Withdrawal to HDFC Bank****XXXX', amount: -45.00, status: 'completed', date: '2026-08-06', time: '09:20', ref: 'BB-WDR-4421' },
  { id: 'tx-005', type: 'bonus', description: 'Referral Reward — Priya Sharma joined', amount: 10.00, status: 'completed', date: '2026-08-05', time: '16:10', ref: 'BB-REF-3310' },
  { id: 'tx-006', type: 'entry', description: 'Entry: Python Data Science Challenge', amount: -15.00, status: 'completed', date: '2026-08-05', time: '10:05', ref: 'BB-ENT-8891' },
  { id: 'tx-007', type: 'prize', description: 'Prize: Speed Math Championship — 3rd Place', amount: 75.00, status: 'completed', date: '2026-08-04', time: '21:45', ref: 'BB-MTH-1192' },
  { id: 'tx-008', type: 'deposit', description: 'Deposit via Razorpay — UPI', amount: 50.00, status: 'completed', date: '2026-08-03', time: '14:30', ref: 'BB-DEP-6654' },
  { id: 'tx-009', type: 'entry', description: 'Entry: UI/UX Mobile App Design Challenge', amount: -10.00, status: 'completed', date: '2026-08-03', time: '09:15', ref: 'BB-ENT-7742' },
  { id: 'tx-010', type: 'withdrawal', description: 'Withdrawal to PayPal — arjun@email.com', amount: -100.00, status: 'pending', date: '2026-08-02', time: '18:00', ref: 'BB-WDR-5598' },
  { id: 'tx-011', type: 'prize', description: 'Prize: JS Coding Sprint — 2nd Place', amount: 175.00, status: 'completed', date: '2026-08-01', time: '20:30', ref: 'BB-COD-8821' },
  { id: 'tx-012', type: 'bonus', description: 'Daily Login Streak Reward — Day 7', amount: 5.00, status: 'completed', date: '2026-07-31', time: '08:00', ref: 'BB-BON-0021' },
  { id: 'tx-013', type: 'entry', description: 'Entry: Business Finance Quiz CFA L1', amount: -5.00, status: 'completed', date: '2026-07-30', time: '11:45', ref: 'BB-ENT-6612' },
  { id: 'tx-014', type: 'refund', description: 'Refund: Cancelled Tournament — Technical Issue', amount: 20.00, status: 'completed', date: '2026-07-29', time: '15:22', ref: 'BB-RFD-1109' },
  { id: 'tx-015', type: 'deposit', description: 'Deposit via Stripe — Mastercard ending 5555', amount: 200.00, status: 'completed', date: '2026-07-28', time: '12:10', ref: 'BB-DEP-5501' },
  { id: 'tx-016', type: 'entry', description: 'Entry: Photography Composition Contest', amount: -5.00, status: 'failed', date: '2026-07-27', time: '17:30', ref: 'BB-ENT-4490' },
];

const typeConfig: Record<string, { icon: React.ElementType; label: string; color: string; amountClass: string }> = {
  prize: { icon: Trophy, label: 'Prize Won', color: '#F59E0B', amountClass: 'tx-credit' },
  entry: { icon: Lock, label: 'Entry Fee', color: '#F43F5E', amountClass: 'tx-debit' },
  deposit: { icon: ArrowDownLeft, label: 'Deposit', color: '#10B981', amountClass: 'tx-credit' },
  withdrawal: { icon: ArrowUpRight, label: 'Withdrawal', color: '#06B6D4', amountClass: 'tx-debit' },
  bonus: { icon: Gift, label: 'Bonus', color: '#A78BFA', amountClass: 'tx-credit' },
  refund: { icon: Unlock, label: 'Refund', color: '#10B981', amountClass: 'tx-credit' },
};

const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  completed: { label: 'Completed', color: '#10B981', bg: 'rgba(16,185,129,0.15)' },
  pending: { label: 'Pending', color: '#F59E0B', bg: 'rgba(245,158,11,0.15)' },
  failed: { label: 'Failed', color: '#F43F5E', bg: 'rgba(244,63,94,0.15)' },
};

const PAGE_SIZE = 8;

export default function TransactionTable({ symbol }: TransactionTableProps) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);

  const filtered = allTransactions.filter((tx) => {
    const matchSearch = tx.description.toLowerCase().includes(search.toLowerCase()) || tx.ref.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === 'all' || tx.type === typeFilter;
    return matchSearch && matchType;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
      {/* Table header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 border-b" style={{ borderColor: 'var(--border)' }}>
        <h3 className="text-base font-bold text-foreground">Transaction History</h3>
        <div className="flex items-center gap-3">
          {/* Type filter */}
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            className="text-sm px-3 py-2 rounded-lg outline-none appearance-none"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
          >
            <option value="all">All Types</option>
            <option value="prize">Prizes</option>
            <option value="deposit">Deposits</option>
            <option value="withdrawal">Withdrawals</option>
            <option value="entry">Entry Fees</option>
            <option value="bonus">Bonuses</option>
            <option value="refund">Refunds</option>
          </select>

          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
            <input
              type="text"
              placeholder="Search transactions..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-8 pr-4 py-2 text-sm rounded-lg outline-none"
              style={{
                background: 'var(--muted)',
                border: '1px solid var(--border)',
                color: 'var(--foreground)',
                width: '200px',
              }}
              onFocus={(e) => { e.target.style.borderColor = 'var(--primary)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; }}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(30,41,59,0.3)' }}>
              {['Type', 'Description', 'Reference', 'Date & Time', 'Status', 'Amount'].map((h) => (
                <th key={`tx-th-${h}`}
                  className="text-left px-4 py-3"
                  style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Filter size={24} style={{ color: 'var(--muted-foreground)' }} />
                    <p className="text-sm font-semibold text-foreground">No transactions found</p>
                    <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Try adjusting your search or filter</p>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((tx) => {
                const tConf = typeConfig[tx.type];
                const sConf = statusConfig[tx.status];
                const isCredit = tx.amount > 0;
                return (
                  <tr
                    key={tx.id}
                    className="border-b transition-colors hover:bg-muted/30"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    {/* Type */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: `${tConf.color}15` }}>
                          <tConf.icon size={14} style={{ color: tConf.color }} />
                        </div>
                        <span className="text-xs font-semibold whitespace-nowrap" style={{ color: tConf.color }}>{tConf.label}</span>
                      </div>
                    </td>

                    {/* Description */}
                    <td className="px-4 py-3.5" style={{ maxWidth: '260px' }}>
                      <p className="text-sm text-foreground font-medium truncate">{tx.description}</p>
                    </td>

                    {/* Reference */}
                    <td className="px-4 py-3.5">
                      <span className="text-xs font-mono px-2 py-1 rounded"
                        style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                        {tx.ref}
                      </span>
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <p className="text-sm text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>{tx.date}</p>
                      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{tx.time}</p>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3.5">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
                        style={{ background: sConf.bg, color: sConf.color }}>
                        {sConf.label}
                      </span>
                    </td>

                    {/* Amount */}
                    <td className="px-4 py-3.5 text-right">
                      <span
                        className="text-sm font-extrabold whitespace-nowrap"
                        style={{
                          color: isCredit ? '#10B981' : '#F43F5E',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {isCredit ? '+' : ''}{symbol}{Math.abs(tx.amount).toFixed(2)}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-5 py-4 border-t" style={{ borderColor: 'var(--border)' }}>
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          Showing <span className="font-semibold text-foreground">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)}</span> of{' '}
          <span className="font-semibold text-foreground">{filtered.length}</span> transactions
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-2 rounded-lg transition-colors hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ border: '1px solid var(--border)' }}
            aria-label="Previous page"
          >
            <ChevronLeft size={14} style={{ color: 'var(--muted-foreground)' }} />
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={`page-${p}`}
              onClick={() => setPage(p)}
              className="w-8 h-8 rounded-lg text-xs font-semibold transition-colors"
              style={{
                background: page === p ? 'var(--primary)' : 'transparent',
                color: page === p ? '#fff' : 'var(--muted-foreground)',
                border: page === p ? 'none' : '1px solid var(--border)',
              }}
            >
              {p}
            </button>
          ))}

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-2 rounded-lg transition-colors hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ border: '1px solid var(--border)' }}
            aria-label="Next page"
          >
            <ChevronRight size={14} style={{ color: 'var(--muted-foreground)' }} />
          </button>
        </div>
      </div>
    </div>
  );
}